import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, User } from 'lucide-react';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Navbar from '../components/Navbar';

const AdminLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, {
        username,
        password
      });

      localStorage.setItem('adminToken', response.data.token);
      localStorage.setItem('adminUsername', response.data.username);
      toast.success('Logged in successfully');
      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Auth error:', error);
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login" data-testid="admin-login-page">
      <Navbar />
      <div className="login-container">
        <div className="login-header">
          <Lock size={40} />
          <h1>Admin Portal</h1>
          <p>Secure Login</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form" data-testid="admin-login-form">
          <div className="form-group">
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              data-testid="input-username"
              placeholder="Enter your username"
            />
          </div>

          <div className="form-group">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="input-password"
              placeholder="Enter your password"
            />
          </div>

          <Button type="submit" disabled={loading} className="login-btn" data-testid="submit-button">
            <User size={18} />
            {loading ? 'Please wait...' : 'Login'}
          </Button>
        </form>
      </div>

      {/* Styles preserved below */}
      <style jsx>{`
        .admin-login {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          background: linear-gradient(135deg, rgba(255, 155, 155, 0.1) 0%, rgba(180, 167, 255, 0.1) 50%, rgba(167, 255, 212, 0.1) 100%);
          padding: 2rem;
          padding-top: 100px;
        }

        .login-container {
          background: white;
          padding: 3rem;
          border-radius: 20px;
          box-shadow: 0 8px 40px rgba(0, 0, 0, 0.1);
          width: 100%;
          max-width: 450px;
          margin: 0 auto;
        }

        .login-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .login-header svg {
          color: #FF9B9B;
          margin-bottom: 1rem;
        }

        .login-header h1 {
          font-size: 2rem;
          font-weight: 700;
          color: #2D3748;
          margin-bottom: 0.5rem;
          font-family: 'Playfair Display', serif;
        }

        .login-header p {
          color: #718096;
        }

        .login-form {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .login-btn {
          width: 100%;
          background: linear-gradient(135deg, #FF9B9B 0%, #B4A7FF 100%);
          color: white;
          padding: 1rem;
          border-radius: 50px;
          border: none;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          box-shadow: 0 4px 20px rgba(255, 155, 155, 0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .login-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 30px rgba(255, 155, 155, 0.4);
        }

        .login-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default AdminLogin;
